import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { MonoLabel, SectionCap, EditorialButton, EmptyState } from '../Editorial';
import CollegeLogo from '../CollegeLogo';
import MatchDetailModal from '../MatchDetailModal';
import LockedMatches from './LockedMatches';

/* Matches list, extracted from MatchesPage. Self-contained; the Recruit shell
   owns the page chrome. */

const DIMENSION_LABELS = {
  major: 'Major',
  sport: 'Sport',
  region: 'Region',
  size: 'Size',
  setting: 'Setting',
  type: 'Public/Private',
  division: 'Division',
  academics: 'Academics',
  roster_opportunity: 'Roster Spots',
  program_investment: 'Well-Resourced',
  scholarship: 'Athletic Aid',
  title_ix: "Women's Program",
  aid_fit: 'Aid Fit',
};

const getAxiosConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function MatchesPanel() {
  const { API_URL } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [total, setTotal] = useState(0);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [acted, setActed] = useState({}); // unitid -> 'following'
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [searchParams] = useSearchParams();

  // Review affordance: ?peek=<unitid> opens the detail modal for any school
  // (the modal falls back to its detail payload for the header). Carried over
  // from /matches, whose redirect preserves the param.
  useEffect(() => {
    const peek = searchParams.get('peek');
    if (peek) setSelectedMatch({ unitid: Number(peek) });
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/athletes/matches?limit=60`, getAxiosConfig());
        if (!cancelled) {
          setMatches(res.data?.matches || []);
          setTotal(res.data?.total ?? (res.data?.matches || []).length);
          setLocked(Boolean(res.data?.locked));
        }
      } catch (err) {
        console.error('Error fetching matches:', err);
        if (!cancelled) {
          setMessage('Could not load your matches.');
          setMessageType('error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API_URL]);

  const toast = (text, type = '') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  // Following is the only bucket a match can be added to. "Interested" was
  // retired from iOS and Android in 274cf96c and web was the last client still
  // offering it; the backend GET still serves existing rows, but nothing
  // writes new ones. See docs/RECRUIT-PARITY-AUDIT-2026-08-10.md, F5.
  const addToFollowing = async (match) => {
    try {
      await axios.post(`${API_URL}/athletes/following`, {
        unitid: match.unitid,
        college_name: match.name,
        college_state: match.state,
      }, getAxiosConfig());
      setActed((prev) => ({ ...prev, [match.unitid]: 'following' }));
      toast('Added to Following');
    } catch (err) {
      console.error('Error adding to following:', err);
      toast('Could not add — it may already be on your list.', 'error');
    }
  };

  const matchedCount = (m) => m.reasons.filter((r) => r.matched).length;

  if (!loading && locked) {
    return <LockedMatches total={total} onUpgrade={() => navigate('/upgrade')} />;
  }

  return (
    <>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      <SectionCap label="Your Matches" hint={matches.length ? `TOP ${matches.length}` : ''} />

      {loading && <p className="ed-muted" style={{ padding: '8px 0' }}>Finding your best-fit schools…</p>}

      {!loading && matches.length === 0 && (
        <EmptyState
          title="No matches yet."
          body="Set your college preferences (major, region, size and more) and we'll rank the schools that fit."
        />
      )}

      {!loading && matches.map((m) => (
        <div
          className="ed-card"
          key={m.unitid}
          role="button"
          tabIndex={0}
          aria-label={`View match details for ${m.name}`}
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedMatch(m)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSelectedMatch(m);
            }
          }}
        >
          <div className="ed-row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <CollegeLogo schoolName={m.name} logoUrl={m.logo_url} unitid={m.unitid} size={48} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ed-row ed-row--between" style={{ gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '-0.2px' }}>{m.name}</h3>
                <MonoLabel>{matchedCount(m)} OF {m.reasons.length} MATCH</MonoLabel>
              </div>
              {m.state && <div className="ed-muted" style={{ fontSize: 13, marginTop: 2 }}>{m.state}</div>}

              <div className="ed-row" style={{ flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {m.reasons.map((r) => (
                  <span
                    key={r.dimension}
                    className={`ed-pill ${r.matched ? 'ed-pill--green' : 'ed-pill--ghost'}`}
                    style={{ fontSize: 11, padding: '3px 9px', cursor: 'default' }}
                  >
                    {r.matched ? '✓' : '✗'} {DIMENSION_LABELS[r.dimension] || r.dimension}
                  </span>
                ))}
              </div>

              {m.earnings_median != null && (
                <div className="ed-muted" style={{ fontSize: 12, marginTop: 8 }}>
                  Typical grad earnings: ${m.earnings_median.toLocaleString()}/yr
                </div>
              )}

              <div className="ed-row" style={{ gap: 8, marginTop: 12 }}>
                <EditorialButton
                  variant={acted[m.unitid] === 'following' ? 'ghost' : ''}
                  onClick={(e) => { e.stopPropagation(); addToFollowing(m); }}
                  disabled={acted[m.unitid] === 'following'}
                >
                  {acted[m.unitid] === 'following' ? '✓ Following' : '+ Follow'}
                </EditorialButton>
              </div>
            </div>
          </div>
        </div>
      ))}

      {selectedMatch && (
        <MatchDetailModal match={selectedMatch} onClose={() => setSelectedMatch(null)} />
      )}
    </>
  );
}
